/* From our community — UGC video. Added by Oleg @ IAKOE.

   Background autoplay (section setting "Autoplay videos"), mirroring the kit's
   ia-video AutoplayStrategy without pulling the component in: ia-video renders its
   video_tag with no `preload` and exposes no param for one, and it ships its own play
   UI that would fight the card's.

   Two states, same as the kit's:
     preview — muted + looping, play icon VISIBLE (it reads as "tap for sound")
     sound   — unmuted, icon hidden; every other card drops back to preview
   Only what's on screen plays (IntersectionObserver), and nothing downloads before the
   page has finished: the markup carries preload="none", so the first play() is what
   starts the fetch. */
if (!customElements.get('ts-community')) {
  customElements.define(
    'ts-community',
    class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
        // pause/ended don't bubble — listen in the capture phase
        this.addEventListener('pause', this.onStop.bind(this), true);
        this.addEventListener('ended', this.onStop.bind(this), true);

        if (this.dataset.autoplay === 'true') this.startWhenPageIsLoaded();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      /* These are decorative clips: they must not compete with the real content for
         bandwidth, so nothing starts until the page itself is done. */
      startWhenPageIsLoaded() {
        if (document.readyState === 'complete') {
          this.observeViewport();
        } else {
          window.addEventListener('load', () => this.observeViewport(), { once: true });
        }
      }

      observeViewport() {
        // Respecting the pref switches the MOTION off, not the feature: the card stays
        // clickable and plays with sound on demand.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target.querySelector('video');
              if (!video) return;
              if (entry.isIntersecting) {
                this.toPreview(entry.target, video, true);
              } else {
                video.pause();
                this.toPreview(entry.target, video, false);
              }
            });
          },
          { threshold: 0.25 }
        );

        this.querySelectorAll('[data-ts-video]').forEach((m) => this.observer.observe(m));
      }

      /* Muted and looping in the background; the icon stays up as the sound affordance. */
      toPreview(media, video, play) {
        video.muted = true;
        media.classList.remove('is-playing');
        if (play && video.paused) {
          // play() rejects on a backgrounded tab or blocked autoplay — harmless, the
          // card just sits on its poster.
          video.play().catch(() => {});
        }
      }

      onClick(event) {
        const media = event.target.closest('[data-ts-video]');
        if (!media || !this.contains(media)) return;
        const video = media.querySelector('video');
        if (!video) return;

        if (video.muted || video.paused) {
          // preview (or stopped) -> sound
          this.pauseAll(video);
          video.muted = false;
          video.play().catch(() => {});
          media.classList.add('is-playing');
        } else {
          // sound -> stop
          video.pause();
          media.classList.remove('is-playing');
        }
      }

      onKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const media = event.target.closest('[data-ts-video]');
        if (!media) return;
        event.preventDefault();
        media.click();
      }

      onStop(event) {
        const media = event.target.closest && event.target.closest('[data-ts-video]');
        if (media) media.classList.remove('is-playing');
      }

      /* One soundtrack at a time. With autoplay on, the others fall back to muted
         preview instead of stopping dead, so the wall keeps moving. */
      pauseAll(except) {
        this.querySelectorAll('[data-ts-video]').forEach((media) => {
          const v = media.querySelector('video');
          if (!v || v === except) return;
          media.classList.remove('is-playing');
          v.muted = true;
          if (this.dataset.autoplay !== 'true') v.pause();
        });
      }
    }
  );
}
